import React from 'react';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import Botao from '@/components/Botao';
import { mdiPencil, mdiFormatListBulleted } from '@mdi/js';
import { EstruturaCampoCatalogo } from '@tera/shared/src/types/Catalogo';

type Props = {
    instanceId: string;
    label: string;
    value?: string;
    opcoes: EstruturaCampoCatalogo[];
    onChange: (valor: string) => void;
    placeholder?: string;
    carregando?: boolean;
    error?: string;
};

/**
 * Escolhe um campo da fonte, com escape para digitação livre.
 *
 * O seletor cobre o caso normal, mas nunca vira uma prisão: a estrutura pode
 * estar indisponível (integração fora do ar), vir de uma amostra que não
 * trouxe o campo, ou o admin pode querer um JSONPath aninhado que a amostra
 * não oferece. Se o valor guardado não está nas opções, o campo já abre em
 * modo texto — em vez de parecer vazio e ser sobrescrito sem querer.
 */
const SeletorCampo = ({
    instanceId,
    label,
    value,
    opcoes,
    onChange,
    placeholder,
    carregando,
    error,
}: Props) => {
    const conhecido = !value || opcoes.some((o) => o.chave === value);
    const [manual, setManual] = React.useState(!conhecido);

    // Ao trocar de fonte ou recarregar a estrutura, um valor que voltou a ser
    // reconhecido pode sair do modo manual sozinho.
    React.useEffect(() => {
        if (!conhecido) setManual(true);
    }, [conhecido]);

    const semOpcoes = !opcoes.length;
    const emTexto = manual || semOpcoes;

    return (
        <VBox gap={4}>
            <HBox gap={4} stretch>
                <div style={{ width: '100%' }}>
                    {emTexto ? (
                        <CampoTexto
                            instanceId={instanceId}
                            label={label}
                            value={value ?? ''}
                            onChange={onChange}
                            placeholder={placeholder}
                            error={error}
                        />
                    ) : (
                        <CampoSeletor
                            instanceId={instanceId}
                            label={label}
                            nullable
                            options={opcoes.map((o) => ({ id: o.chave, label: o.rotulo }))}
                            value={value || null}
                            onChange={(v: string | null) => onChange(v ?? '')}
                            loadingState={carregando ? 'loading' : 'success'}
                            error={error}
                        />
                    )}
                </div>
                {!semOpcoes && (
                    <div style={{ width: 'auto', alignSelf: 'flex-end' }}>
                        <Botao
                            variant="none-primary"
                            title={manual ? 'Escolher da lista' : 'Digitar manualmente'}
                            icon={manual ? mdiFormatListBulleted : mdiPencil}
                            onClick={() => setManual(!manual)}
                        />
                    </div>
                )}
            </HBox>
        </VBox>
    );
};

export default SeletorCampo;
